import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, SESSAO_EXPIRADA, tokens } from "@/shared/api/http";

export interface UsuarioSessao {
  tipo: "interno";
  id: string;
  nome: string;
  email: string;
  papel: string;
  /** Senha definida por outra pessoa: o sistema pede uma nova antes de liberar as telas. */
  senhaTemporaria?: boolean;
}

interface SessionValue {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Troca a própria senha; as outras sessões caem, esta continua. */
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const expirou = () => setUsuario(null);
    window.addEventListener(SESSAO_EXPIRADA, expirou);
    let ativo = true;
    // Ao abrir: confere a sessão. Se a API estiver fora (reiniciando, sem rede), tenta de novo por
    // alguns segundos em vez de mandar para o login; só sessão recusada pela API desloga.
    async function conferir(tentativa = 0): Promise<void> {
      try {
        const u = await api<UsuarioSessao>("/auth/me");
        if (ativo) setUsuario(u);
      } catch (e) {
        const recusada = e instanceof ApiError && (e.status === 401 || e.status === 403);
        if (!recusada && tentativa < 5 && ativo) {
          await new Promise((r) => setTimeout(r, 2000));
          return conferir(tentativa + 1);
        }
        if (recusada) tokens.clear();
      }
    }
    if (tokens.get()) void conferir().finally(() => ativo && setCarregando(false));
    else setCarregando(false);
    return () => {
      ativo = false;
      window.removeEventListener(SESSAO_EXPIRADA, expirou);
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const res = await api<{ accessToken: string; refreshToken: string }>("/auth/login", { body: { email, senha } });
    tokens.set(res);
    setUsuario(await api<UsuarioSessao>("/auth/me"));
  }, []);

  const sair = useCallback(async () => {
    const t = tokens.get();
    tokens.clear();
    setUsuario(null);
    if (t) await api("/auth/logout", { body: { refreshToken: t.refreshToken } }).catch(() => undefined);
  }, []);

  const trocarSenha = useCallback(async (senhaAtual: string, novaSenha: string) => {
    await api("/auth/senha", { body: { senhaAtual, novaSenha, refreshToken: tokens.get()?.refreshToken } });
    setUsuario(await api<UsuarioSessao>("/auth/me"));
  }, []);

  return <SessionContext.Provider value={{ usuario, carregando, entrar, sair, trocarSenha }}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession fora do SessionProvider");
  return ctx;
}
